import { Card, CardBody, Table, TableContainer, Thead, Tbody, Th, Tr, Td, Input, Select, Checkbox, Stack, Spinner, Radio, RadioGroup, Button, Box, Link, useToast,
    Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverCloseButton, PopoverHeader, PopoverBody, Progress, Text, Tabs, TabList, Tab, TabPanels, TabPanel,
    Stat, StatNumber, StatHelpText, StatLabel,
    Divider
 } from '@chakra-ui/react'
import { ArrowUpDownIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'

import { useParams } from 'react-router-dom'
import Pagination from 'components/Pagination/Pagination'
import { useState, useEffect, useCallback, useRef } from 'react'
import * as ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { api, baseURL } from 'apis/configs/axiosConfigs'


const ExpectedItemsComparison = ({ keyBy, refreshKey }) => {
    const { countingId } = useParams()
    const toast = useToast()
    const [ items, setItems ] = useState([])
    const [ departments, setDepartments ] = useState([])
    const [ countedItems, setCountedItems ] = useState({})
    const [ currentPage, setCurrentPage ] = useState(1)
    const [ filters, setFilters ] = useState({
        upc: '',
        mkt_1: '',
        description: '',
        department: '',
        amount: '',
        price_buy: '',
        price_sell: ''
    })

    const [ isFileLoading, setFileLoading ] = useState(false)
    const [ deletingExpectationId, setDeletingExpectationId ] = useState(null)
    const [ updatingExpectationId, setUpdatingExpectationId ] = useState(null)
    const [ isDeletingAllExpectations, setIsDeletingAllExpectations ] = useState(false)
    const [ isRecalculatingCountedAmounts, setIsRecalculatingCountedAmounts ] = useState(false)

    const [ sortConfig, setSortConfig ] = useState({ sort: null, sortOrder: '' })

    const toggleSortOrder = (field) => {
        if (sortConfig.sort === field) {
            setSortConfig({
                ...sortConfig,
                sortOrder: sortConfig.sortOrder === 'asc' ? 'desc' : 'asc'
            })
        } else {
            setSortConfig({
                sort: field,
                sortOrder: 'asc'
            })
        }
    }

    const handlePageChange = (data) => {
        fetchItems(data)
        setCurrentPage(data.page)
    }

    const handleFilterChange = (type, value) => {
        setFilters((currentFilters) => ({ ...currentFilters, [type]: value }))
        setCurrentPage(1)
        fetchItems({ page: 1, [type]: value })
    }

    const [ headerRow, setHeaderRow ]  = useState([ 
        'upc', 'mkt_1', 'description',
        'department',
        'amount', 'counted_amount', 'gap',
        'price_buy', 'total_buy', 'money_gap_buy',
        'price_sell', 'total_sell', 'money_gap_sell',
    ])

    const headersHebrew = {
        money_gap_buy: 'סה"כ הפרש מחיר עלות',
        total_buy: 'סה"כ מחיר עלות',
        price_buy: 'מחיר עלות',
        money_gap_sell: 'סה"כ הפרש מחיר מכירה ',
        total_sell: 'סה"כ מחיר מכירה',
        price_sell: 'מחיר מכירה',
        gap: 'הפרש',
        counted_amount: 'מלאי ספור',
        amount: 'כמות מלאי',
        department: 'מחלקה',
        description: 'תאור פריט',
        mkt_1: 'מק"ט',
        upc: 'ברקוד'
    }

    const sortable = ['upc', 'mkt_1', 'description', 'department', 'amount', 'price_buy', 'price_sell', 'gap', 'counted_amount', 'total_buy', 'money_gap_buy', 'total_sell', 'money_gap_sell']

    const formatRow = ({ upc, mkt_1, description, department, amount, price_buy, price_sell }, allCountedItems = null, currency = null) => {
        const counted = allCountedItems ? allCountedItems[keyBy === 'upc' ? upc : mkt_1] : null
        const count = counted?.amount ?? 0
        const gap = count - amount
        return [
            keyBy === 'upc' ? upc : counted?.upc,
            keyBy === 'mkt_1' ? mkt_1 : counted?.mkt_1,
            description,
            department, 
            amount, count, gap,
            ...(price_buy ? [price_buy, amount * price_buy, gap * price_buy].map((value) => Number(value.toFixed(2)) + (currency ? ' ' + currency : '')) : []),
            ...(price_sell ? [price_sell, amount * price_sell, gap * price_sell].map((value) => Number(value.toFixed(2)) + (currency ? ' ' + currency : '')) : []),           
        ]
    }

    const formatRowEx = ({upc, mkt_1, description, department, amount, price_buy, price_sell, counted_amount, gap, total_sell, money_gap_sell, total_buy, money_gap_buy}, includePriceBuy, includePriceSell) => {
        return [
            upc, mkt_1, description, 
            department, 
            amount, counted_amount, gap, 
            ...(includePriceBuy ? [price_buy ?? '', total_buy ?? '', money_gap_buy ?? ''] : []),
            ...(includePriceSell ? [price_sell ?? '', total_sell ?? '', money_gap_sell ?? ''] : [])
        ]
    }

    const filtersConfig = [
        keyBy === 'upc' 
            ? {type: 'upc', placeholder: 'UPC', isSelect: false}
            : {type: 'mkt_1', placeholder: 'MKT', isSelect: false},
        {type: 'description', placeholder: 'Description', isSelect: false},
        {type: 'department', placeholder: 'Department', isSelect: true},
        {type: 'amount', placeholder: 'Amount', isSelect: false},
        {type: 'price_buy', placeholder: 'Price Buy', isSelect: false},
        {type: 'price_sell', placeholder: 'Price Sell', isSelect: false}
    ]

    const fetchItems = async (params = {}) => {
        const activeFilters = Object.fromEntries(
            filtersConfig
                .map(({ type }) => [type, filters[type]])
                .filter(([, value]) => value !== '')
        )

        return api.request({
            url: `/counting/expectations/${countingId}`,
            method: 'GET',
            params: { page: currentPage, keyBy, ...activeFilters, ...params, ...( sortConfig.sort && sortConfig  ) }
        }).then( (resp) => {
            setItems(resp.data)
            setDepartments(resp.data.departments ?? [])
            
            resp.data.counted_items?.length !== 0 && setCountedItems(resp.data.counted_items)
            
            if (resp.dynamicHeaderRow) {
                setHeaderRow((headerRow) => [...headerRow, ...resp.dynamicHeaderRow])
            }
        })
    }

    const deleteExpectation = async (expectationId) => {
        setDeletingExpectationId(expectationId)

        try {
            await api.request({
                url: `/counting/expectations/${countingId}/${expectationId}`,
                method: 'DELETE'
            })
            await fetchItems()
        } finally {
            setDeletingExpectationId(null)
        }
    }

    const updateDeletionProtection = async (expectationId, isDeletionProtected) => {
        setUpdatingExpectationId(expectationId)

        try {
            await api.request({
                url: '/counting/expectations/update',
                method: 'PATCH',
                data: {
                    _id: expectationId,
                    is_deletion_protected: isDeletionProtected
                }
            })

            setItems((currentItems) => ({
                ...currentItems,
                data: currentItems.data?.map((item) => item._id === expectationId
                    ? { ...item, is_deletion_protected: isDeletionProtected }
                    : item
                )
            }))
        } finally {
            setUpdatingExpectationId(null)
        }
    }

    const deleteAllExpectations = async () => {
        setIsDeletingAllExpectations(true)

        try {
            await api.request({
                url: `/counting/expectations/${countingId}`,
                method: 'DELETE'
            })
            setCurrentPage(1)
            await fetchItems({ page: 1 })
        } finally {
            setIsDeletingAllExpectations(false)
        }
    }

    const recalculateCountedAmounts = async () => {
        setIsRecalculatingCountedAmounts(true)

        try {
            const response = await api.request({
                url: `/counting/expectations/${countingId}/recalculate-counted-amounts`,
                method: 'POST',
                params: { keyBy }
            })

            toast({
                description: `${response.data.updated} items are updated.`,
                status: 'success',
                duration: 5000,
                isClosable: true
            })
            await fetchItems()
        } catch (error) {
            toast({
                description: error.response?.data?.message || 'Unable to recalculate counted amounts.',
                status: 'error',
                duration: 5000,
                isClosable: true
            })
        } finally {
            setIsRecalculatingCountedAmounts(false)
        }
    }

    useEffect(() => {
        fetchItems()
    }, [keyBy, sortConfig, refreshKey])

    const downloadFile = async () => {
        setFileLoading(true)

        const workbook = new ExcelJS.Workbook()
        const worksheet = workbook.addWorksheet('Data')


        const allItems = await api.request({
            url: `/counting/expectations/${countingId}/download`,
            method: 'GET',
            params: { keyBy }
        })

        const downloadItems = allItems.data.data ?? []
        const includePriceBuy = Boolean(downloadItems[0]?.price_buy)
        const includePriceSell = Boolean(downloadItems[0]?.price_sell)

        worksheet.addRow(
            headerRow.filter(header => ['money_gap_buy', 'total_buy', 'price_buy'].includes(header) ? includePriceBuy
                : (['money_gap_sell', 'total_sell', 'price_sell'].includes(header) ? includePriceSell : true)
            ).map(header => headersHebrew[header] || header)
        )

        downloadItems.forEach(item => worksheet.addRow(formatRowEx(item, includePriceBuy, includePriceSell)))
        worksheet.views = [{ state: 'frozen', ySplit: 1 }]
        workbook.xlsx.writeBuffer().then(buffer => saveAs(new Blob([buffer], 
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'supply_gap.xlsx')
        )
        setFileLoading(false)
    }

    const totalsHelpText = [
        items.totals?.money_gap_buy != null && Number(items.totals.money_gap_buy) !== 0
            ? `buy - ${items.totals.money_gap_buy.toFixed(2)}`
            : null,
        items.totals?.money_gap_sell != null && Number(items.totals.money_gap_sell) !== 0
            ? `sell ${items.totals.money_gap_sell.toFixed(2)}`
            : null
    ].filter(Boolean).join(', ')

    return (
        <>
                <Stack direction='row' spacing={4} justifyContent='space-between' alignItems='center' mb={4}>
                    {items.totals && (
                        <Stack direction='row' spacing={4} alignItems='stretch'>
                            <Stat borderWidth='1px' borderRadius='md' px={4} py={2} width='max-content' flex='none'>
                                <StatLabel>Total Gap</StatLabel>
                                <StatNumber>{items.totals.gap}</StatNumber>
                                {totalsHelpText && <StatHelpText mb={0}>{totalsHelpText}</StatHelpText>}
                            </Stat>
                            <Stat borderWidth='1px' borderRadius='md' px={4} py={2} width='max-content' flex='none'>
                                <StatLabel>Total Amount</StatLabel>
                                <StatNumber>{items.totals.amount}</StatNumber>
                            </Stat>
                        </Stack>
                    )}
                    <Divider orientation='vertical' />
                    <Stack direction='row' spacing={4} ml='auto'>
                        <Button onClick={downloadFile} size='sm' colorScheme='teal' isLoading={isFileLoading} loadingText='Downloading...'>Download</Button>
                        <Button
                            onClick={recalculateCountedAmounts}
                            size='sm'
                            colorScheme='blue'
                            isLoading={isRecalculatingCountedAmounts}
                            loadingText='Recalculating...'
                        >
                            Recalculate Counted Amounts
                        </Button>
                        <Button
                            onClick={deleteAllExpectations}
                            size='sm'
                            colorScheme='red'
                            isLoading={isDeletingAllExpectations}
                            loadingText='Deleting...'
                            isDisabled={deletingExpectationId !== null}
                        >
                            Delete All
                        </Button>
                    </Stack>
                </Stack>

                <TableContainer>
                    <Table>
                        {/* <TableCaption placement='top'>Supply Gap</TableCaption> */}
                        <Thead>
                            <Tr>
                                {filtersConfig.map((filter, idx) => <> 
                                    { keyBy !== 'upc' && idx === 0 && <Th key={idx+'-'}><div></div></Th> } 
                                    <Th key={'h'+ idx}>
                                        {filter.isSelect ? (
                                            <Select
                                                value={filters[filter.type]}
                                                placeholder={filter.placeholder}
                                                onChange={(event) => handleFilterChange(filter.type, event.target.value)}
                                            >
                                                {departments.map((department) => (
                                                    <option key={department} value={department}>{department}</option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Input
                                                value={filters[filter.type]}
                                                placeholder={filter.placeholder}
                                                onChange={(event) => handleFilterChange(filter.type, event.target.value)}
                                            />
                                        )}
                                    </Th>
                                    { keyBy === 'upc' && idx === 0 && <Th key={idx+'-'}><div></div></Th> }
                                    </>
                                )}
                                <Th />
                                <Th />
                            </Tr>
                            <Tr>
                                { headerRow.filter(header =>
                                    ['money_gap_buy', 'total_buy', 'price_buy'].includes(header) ? items.data?.some(item => item.price_buy)
                                    : (['money_gap_sell', 'total_sell', 'price_sell'].includes(header) ? items.data?.some(item => item.price_sell) : true)
                                ).map((header, idx) => (
                                     <Th key={'h-' + idx}> { sortable.includes(header) 
                                        ? <Box onClick={() => toggleSortOrder(header)} style={{cursor: 'pointer'}}>
                                            {header}
                                            {sortConfig.sort === header ? (
                                                sortConfig.sortOrder === 'asc' ? <ChevronDownIcon boxSize={4}/> : <ChevronUpIcon boxSize={4}/>
                                            ) : <ArrowUpDownIcon boxSize={4} />}
                                        </Box>
                                        : header }
                                     </Th>
                                )) }
                                <Th>Protected</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {items.data?.map((item) => 
                                <Tr key={item._id}> 
                                    { formatRow(item, countedItems, '₪').map((cell, idx) => idx !== (keyBy === 'upc' ? 0 : 1)  ?
                                    <Td key={item._id + '_' + idx}>{cell}</Td> :
                                    <Td key={item._id + '_' + idx}>
                                        <Popover key={'pop' + item._id + idx} placement='right'>
                                            <PopoverTrigger>
                                                <Box
                                                    tabIndex='0'
                                                    role='button'
                                                    aria-label='Some box'
                                                    p={5}
                                                    // w='120px'
                                                    // bg='gray.300'
                                                >{cell}</Box>
                                            </PopoverTrigger>
                                            <PopoverContent w='400' bgGradient='linear(to-t, green.50, green.700)'>
                                                <PopoverArrow />
                                                <PopoverCloseButton />
                                                <PopoverHeader>Rows</PopoverHeader>
                                                <PopoverBody>
                                                    <p>{countedItems[cell]?.row_names.join(', ')}</p>
                                                </PopoverBody>
                                            </PopoverContent>
                                        </Popover>
                                    </Td>)
                                        
                                    }
                                    <Td>
                                        <Checkbox
                                            aria-label={`Protect expectation ${item._id} from deletion`}
                                            isChecked={Boolean(item.is_deletion_protected)}
                                            isDisabled={updatingExpectationId !== null}
                                            onChange={(event) => updateDeletionProtection(item._id, event.target.checked)}
                                        />
                                    </Td>
                                    <Td>
                                        <Button
                                            size='sm'
                                            colorScheme='red'
                                            isLoading={deletingExpectationId === item._id}
                                            isDisabled={deletingExpectationId !== null || isDeletingAllExpectations}
                                            onClick={() => deleteExpectation(item._id)}
                                        >
                                            Delete
                                        </Button>
                                    </Td>
                                </Tr>)}
                        </Tbody>
                    </Table>
                    <Pagination lastPage={items.last_page ?? 1 } getData={handlePageChange} />
                </TableContainer>
        </>
    )
}

const UnmatchedCountedItems = ({ keyBy, refreshKey }) => {
    const { countingId } = useParams()
    const [items, setItems] = useState([])
    const [total, setTotal] = useState(0)
    const [lastPage, setLastPage] = useState(1)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const [filters, setFilters] = useState({ upc: '', mkt_1: '' })
    const filtersRef = useRef({ upc: '', mkt_1: '' })
    const requestControllerRef = useRef(null)

    const fetchItems = useCallback(async ({ page = 1 } = {}) => {
        requestControllerRef.current?.abort()
        const controller = new AbortController()
        requestControllerRef.current = controller

        setIsLoading(true)
        setError('')

        try {
            const activeFilters = Object.fromEntries(
                Object.entries(filtersRef.current).filter(([, value]) => value.trim() !== '')
            )
            const response = await api.request({
                url: `/counting/expectations/${countingId}/unmatched-counted-items`,
                method: 'GET',
                params: { page, keyBy, ...activeFilters },
                signal: controller.signal
            })
            const responseData = response.data
            setItems(Array.isArray(responseData) ? responseData : responseData.data ?? [])
            setTotal(Array.isArray(responseData) ? responseData.length : responseData.total ?? 0)
            setLastPage(Array.isArray(responseData) ? 1 : responseData.last_page ?? 1)
        } catch (requestError) {
            if (!controller.signal.aborted) {
                setError(requestError.response?.data?.message || requestError.message || 'Unable to load unmatched counted items')
            }
        } finally {
            if (requestControllerRef.current === controller) {
                requestControllerRef.current = null
                setIsLoading(false)
            }
        }
    }, [countingId, keyBy])

    const updateFilter = (field, value) => {
        const updatedFilters = { ...filtersRef.current, [field]: value }
        filtersRef.current = updatedFilters
        setFilters(updatedFilters)
    }

    useEffect(() => {
        fetchItems()

        return () => {
            const controller = requestControllerRef.current
            requestControllerRef.current = null
            controller?.abort()
        }
    }, [fetchItems, refreshKey])

    return (
        <TableContainer>
            <Stack direction='row' justifyContent='space-between' mb={4}>
                <Stat borderWidth='1px' borderRadius='md' px={4} py={2} width='max-content' flex='none'>
                    <StatLabel>Total Unmatched Items</StatLabel>
                    <StatNumber>{total}</StatNumber>
                </Stat>
                <Stack direction='row' spacing={4}>
                    <Input
                        value={filters.upc}
                        placeholder='UPC'
                        size='sm'
                        onChange={(event) => updateFilter('upc', event.target.value)}
                        onBlur={() => fetchItems({ page: 1 })}
                    />
                    <Input
                        value={filters.mkt_1}
                        placeholder='MKT'
                        size='sm'
                        onChange={(event) => updateFilter('mkt_1', event.target.value)}
                        onBlur={() => fetchItems({ page: 1 })}
                    />
                </Stack>
                <Link
                    href={`${(baseURL || '').replace(/\/$/, '')}/counting/expectations/${countingId}/unmatched-counted-items/download?keyBy=${keyBy}`}
                    isExternal
                >
                    <Button size='sm' colorScheme='teal'>Download</Button>
                </Link>
            </Stack>
            {isLoading && <Spinner size='sm' color='blue.500' mb={4} />}
            {error && <Text color='red.600' mb={4}>{error}</Text>}
            <Table>
                <Thead>
                    <Tr>
                        <Th>UPC</Th>
                        <Th>MKT</Th>
                        <Th>Description</Th>
                        <Th>Counted</Th>
                        <Th>Row</Th>
                        <Th>Device</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {items.map((item, index) => (
                        <Tr key={item._id ?? `${item.upc ?? item.mkt_1}-${index}`}>
                            <Td>{item.upc ?? ''}</Td>
                            <Td>{item.mkt_1 ?? ''}</Td>
                            <Td>{item.description ?? ''}</Td>
                            <Td>{item.counted_amount ?? item.amount ?? 0}</Td>
                            <Td>{item.row_name ?? ''}</Td>
                            <Td>{item.device_number ?? ''}</Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
            {!isLoading && !error && items.length === 0 && <Text textAlign='center' py={4}>No unmatched counted items</Text>}
            <Pagination lastPage={lastPage} getData={fetchItems} />
        </TableContainer>
    )
}

export const SupplyGap = () => {
    const { countingId } = useParams()
    const [keyBy, setKeyBy] = useState('upc')
    const [refreshKey, setRefreshKey] = useState(0)
    const [isUpLoading, setIsUpLoading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [uploadStatus, setUploadStatus] = useState('')
    const [uploadError, setUploadError] = useState(false)

    const upload = async (event) => {
        if (!event.target.files.length) return

        const formData = new FormData()
        formData.append('file', event.target.files[0])
        formData.append('keyBy', keyBy)
        setIsUpLoading(true)
        setUploadProgress(0)
        setUploadStatus('Uploading file...')
        setUploadError(false)

        try {
            const apiBaseURL = (baseURL || '').replace(/\/$/, '')
            const response = await fetch(`${apiBaseURL}/counting/expectations/upload/${countingId}?stream=1`, {
                method: 'POST',
                headers: {
                    Accept: 'application/x-ndjson',
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
                body: formData,
            })

            if (!response.ok) throw new Error('Failed to upload expectations')
            if (!response.body) throw new Error('Upload progress stream is not available')

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ''

            const handleProgressEvent = (line) => {
                if (!line.trim()) return
                const progressEvent = JSON.parse(line)
                if (typeof progressEvent.percent === 'number') setUploadProgress(progressEvent.percent)

                if (progressEvent.event === 'started') setUploadStatus('Preparing file...')
                else if (progressEvent.event === 'parsed') setUploadStatus('Updating expectations...')
                else if (progressEvent.event === 'progress') setUploadStatus(`Updated ${progressEvent.processed} of ${progressEvent.total}`)
                else if (progressEvent.event === 'completed') setUploadStatus('Upload completed')
                else if (progressEvent.event === 'error') throw new Error(progressEvent.error || progressEvent.message || 'Upload failed')
            }

            let isStreamDone = false
            while (!isStreamDone) {
                const { value, done } = await reader.read()
                isStreamDone = done
                if (isStreamDone) break
                buffer += decoder.decode(value, { stream: true })
                const lines = buffer.split('\n')
                buffer = lines.pop()
                lines.forEach(handleProgressEvent)
            }

            buffer += decoder.decode()
            handleProgressEvent(buffer)
            setUploadProgress(100)
            setRefreshKey((currentKey) => currentKey + 1)
        } catch (error) {
            setUploadError(true)
            setUploadStatus(error.message || 'Upload failed')
        } finally {
            setIsUpLoading(false)
            event.target.value = ''
        }
    }

    return (
        <Card>
            <CardBody>
                <Stack direction='row' spacing={4} mb={4}>
                    <RadioGroup onChange={setKeyBy} value={keyBy}>
                        <Stack direction='row' spacing={4}>
                            <Radio value='upc'>UPC</Radio>
                            <Radio value='mkt_1'>MKT</Radio>
                        </Stack>
                    </RadioGroup>
                    <Input type='file' onChange={upload} isDisabled={isUpLoading} />
                    <Spinner size='sm' color='blue.500' hidden={!isUpLoading} />
                    <Link
                        href={`${(baseURL || '').replace(/\/$/, '')}/counting/expectations/${countingId}/summary?keyBy=${encodeURIComponent(keyBy)}`}
                        isExternal
                        ml='auto'
                    >
                        <Button size='sm' colorScheme='teal'>Download Summary PDF</Button>
                    </Link>
                </Stack>
                {(isUpLoading || uploadStatus) && <Box mb={4}>
                    <Progress value={uploadProgress} size='sm' colorScheme={uploadError ? 'red' : 'blue'} borderRadius='sm' />
                    <Text mt={1} fontSize='sm' color={uploadError ? 'red.600' : 'gray.600'}>{uploadStatus} ({uploadProgress}%)</Text>
                </Box>}
                <Tabs isFitted variant='enclosed' isLazy>
                    <TabList>
                        <Tab>Expected vs Counted</Tab>
                        <Tab>Unmatched Counted Items</Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel px={0}>
                            <ExpectedItemsComparison keyBy={keyBy} refreshKey={refreshKey} />
                        </TabPanel>
                        <TabPanel px={0}>
                            <UnmatchedCountedItems keyBy={keyBy} refreshKey={refreshKey} />
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </CardBody>
        </Card>
    )
}
